<template>
  <div>
    <ContentLoop
      :rows="landingPage.rows"
      :active-variant-sku="landingPage.productPanel ? activeVariantSku : ''"
      :narrow-content="landingPage.narrowContent"
    />
  </div>
</template>

<script>
import landingPageQuery from '~/gql/landingPage.gql'
import { setPageTheme } from '~/assets/js/helpers/pageThemes'
import { resolveUrlParams } from '~/assets/js/helpers/url'

export default defineNuxtComponent({
  async setup() {
    const route = useRouter().currentRoute.value
    const { $sentry, $product, $head, runWithContext } = useNuxtApp()
    const pageStore = usePageStore()
    const { localeForDato, getCorrectSlugForLocale } = useDatoLocale()
    const previewSlug = route.params?.genericlandingpage?.join('__')
    const slug =
      previewSlug ??
      getCorrectSlugForLocale(
        route.meta?.slugLocaleMapping,
        route.params?.genericlandingpage?.join('__') || route.meta?.slug
      )
    const id = route?.meta?.id

    const variables = {
      locale: localeForDato,
      slug
    }

    if (id) {
      variables.id = id
    }

    const { data: result, error } = await useDatoQuery({
      query: landingPageQuery,
      variables,
      cachePolicy: 'network-only'
    })

    if (error.value) {
      $sentry.captureException(error)
      throw createError({
        statusCode: error.value.statusCode,
        statusMessage: error.value.statusMessage,
      })
    }

    if (!result.value.landingPage) {
      throw createError({
        statusCode: 404,
        statusMessage: `Landing page not found: ${slug}`,
        fatal: true
      })
    }

    const localisedSlug = previewSlug
      ? result.value.localisedLandingPage?.slug
      : null

    if (localisedSlug && localisedSlug !== slug) {
      return navigateTo(
        resolveUrlParams(
          route.path.replace(new RegExp(`/${slug}[/$]*`), `/${localisedSlug}`),
          route.query
        ),
        { redirectCode: 302 }
      )
    }

    const activeVariantSku =
      route.meta.sku ||
      (result.value.landingPage?.products?.length
        ? result.value.landingPage.products[0].sku
        : null)

    const activeVariant =
      result.value.landingPage?.products?.find(
        (product) => product.sku === activeVariantSku
      ) || {}

    const pageTheme =
      activeVariant.pageThemeOverride ||
      result.value.landingPage.pageTheme ||
      null
    await setPageTheme(pageStore, pageTheme)

    const setupPage = async () => {
      await $product.getFromLandingPageResponse(result.value.landingPage)
      pageStore.togglePageFooter(result.value.landingPage.hidePageFooter)
      runWithContext(() => useHead($head(result.value.landingPage.meta)))
    }
    await setupPage()

    watch(result, setupPage)

    return { result, activeVariantSku, pageStore }
  },

  computed: {
    landingPage () {
      return this.result.landingPage
    },
    activeVariant() {
      return (
        this.landingPage.products.find(
          (product) => product.sku === this.activeVariantSku
        ) || {}
      )
    },
  },

  async mounted() {
    // Scroll to a row on this page
    if (this.$route.query.scroll) {
      // Get a list of row ids from the URL, map to DOM ids and filter to those that exist and are visible
      const scrollTargets = this.$route.query.scroll
        .split(',')
        .map((id) => `#row-${id}`)
        .filter((id) => (document.querySelector(id) || {}).offsetParent)

      // If there are any valid scrollTargets then scroll to the first
      if (scrollTargets)
        this.$scrollTo(scrollTargets[0], {
          duration: 600,
          force: false,
          offset: -200,
        })
    }
  },

  methods: {
    setActiveVariant(variant) {
      this.activeVariantSku = variant.sku
    },
    setPageTheme() {
      setPageTheme(
        this.pageStore,
        this.activeVariant.pageThemeOverride || this.pageTheme
      )
    },
  },
})
</script>
